import { useCallback, useEffect, useRef, useState } from 'react';
const DRAWER_WIDTH_KEY = 'drawer-width';
/** Arrow-key steps on a focused handle; Shift takes the bigger one. */ const KEY_STEP = 16;
const KEY_STEP_LARGE = 64;
const RESIZE_DEBOUNCE_MS = 150;
/**
 * Get the maximum allowed drawer width based on viewport size and responsive mode
 */ const getMaxAllowedWidth = (viewportWidth, responsive, maxWidth, fraction = 0.9)=>{
    if (responsive) {
        // Mobile: full width
        if (viewportWidth < 768) {
            return viewportWidth;
        }
        // Tablet: 70% of viewport
        if (viewportWidth < 1024) {
            return Math.floor(viewportWidth * 0.7);
        }
    }
    // Desktop or non-responsive: a share of the viewport, capped at maxWidth
    return Math.min(Math.floor(viewportWidth * fraction), maxWidth);
};
/**
 * Clamp width to valid range based on constraints
 */ const clampWidth = (width, minWidth, maxWidth, responsive, viewportWidth, fraction)=>{
    const maxAllowed = getMaxAllowedWidth(viewportWidth, responsive, maxWidth, fraction);
    return Math.max(minWidth, Math.min(maxAllowed, width));
};
/**
 * Hook to handle drawer resize functionality
 * - Manual resize via drag handle
 * - Automatic adjustment on window resize
 * - Persists width to localStorage
 * - Responsive breakpoints (optional)
 */ export const useDrawerResize = (options = {})=>{
    const { minWidth = 300, maxWidth = 800, defaultWidth = 560, onWidthChange, responsive = false, storageKey = DRAWER_WIDTH_KEY, maxViewportFraction: fraction } = options;
    const [handleElement, setHandleElement] = useState(null);
    const isResizingRef = useRef(false);
    const startXRef = useRef(0);
    const startWidthRef = useRef(0);
    const currentWidthRef = useRef(defaultWidth);
    const resizeTimeoutRef = useRef(null);
    // Callback ref to capture the handle element
    const resizeHandleRef = useCallback((node)=>{
        setHandleElement(node);
    }, []);
    // Restore width from localStorage on mount
    useEffect(()=>{
        let initialWidth = defaultWidth;
        try {
            const storedWidth = localStorage.getItem(storageKey);
            if (storedWidth) {
                const parsed = Number(storedWidth);
                if (!Number.isNaN(parsed)) {
                    initialWidth = parsed;
                }
            }
        } catch  {
        // localStorage access failed - use default width
        }
        // Clamp to current constraints
        const viewportWidth = window.innerWidth;
        const clampedWidth = clampWidth(initialWidth, minWidth, maxWidth, responsive, viewportWidth, fraction);
        currentWidthRef.current = clampedWidth;
        onWidthChange?.(clampedWidth);
    }, [
        defaultWidth,
        fraction,
        maxWidth,
        minWidth,
        onWidthChange,
        responsive,
        storageKey
    ]); // Only run on mount
    // Save width to localStorage
    const persistWidth = useCallback((width)=>{
        try {
            localStorage.setItem(storageKey, String(width));
        } catch  {
        // localStorage access failed - silently ignore
        }
    }, [
        storageKey
    ]);
    // Handle manual resize via drag
    useEffect(()=>{
        let dragShield = null;
        const removeDragShield = ()=>{
            dragShield?.remove();
            dragShield = null;
        };
        const handleMouseMove = (e)=>{
            if (!isResizingRef.current) return;
            const deltaX = startXRef.current - e.clientX;
            let newWidth = startWidthRef.current + deltaX;
            // Clamp width between min and max, respecting viewport constraints
            const viewportWidth = window.innerWidth;
            newWidth = clampWidth(newWidth, minWidth, maxWidth, responsive, viewportWidth, fraction);
            currentWidthRef.current = newWidth;
            onWidthChange?.(newWidth);
        };
        const handleMouseUp = ()=>{
            isResizingRef.current = false;
            removeDragShield();
            // Persist final width
            persistWidth(currentWidthRef.current);
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
        const handleMouseDown = (e)=>{
            if (e.button !== 0) return; // Only left click
            e.preventDefault(); // Prevent text selection during drag
            e.stopPropagation(); // Stop event from bubbling
            isResizingRef.current = true;
            startXRef.current = e.clientX;
            // CSS can cap the panel below its requested width. Start from the
            // visible width so reversing a drag responds immediately.
            startWidthRef.current = handleElement?.parentElement?.getBoundingClientRect().width || currentWidthRef.current;
            // An embedded editor owns mouse events inside its iframe. Cover it only
            // during the drag so move/up stay in this document in both directions.
            removeDragShield();
            dragShield = document.createElement('div');
            dragShield.setAttribute('aria-hidden', 'true');
            Object.assign(dragShield.style, {
                position: 'fixed',
                inset: '0',
                zIndex: '2147483647',
                cursor: 'col-resize',
                userSelect: 'none'
            });
            document.body.appendChild(dragShield);
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
        };
        // The handle sits on the panel's left edge: Left widens, Right narrows,
        // Home/End jump to the limits. Same clamping and persistence as a drag.
        const handleKeyDown = (e)=>{
            const step = e.shiftKey ? KEY_STEP_LARGE : KEY_STEP;
            const viewportWidth = window.innerWidth;
            let next = null;
            if (e.key === 'ArrowLeft') next = currentWidthRef.current + step;
            else if (e.key === 'ArrowRight') next = currentWidthRef.current - step;
            else if (e.key === 'Home') next = minWidth;
            else if (e.key === 'End') next = maxWidth;
            if (next === null) return;
            e.preventDefault();
            const width = clampWidth(next, minWidth, maxWidth, responsive, viewportWidth, fraction);
            currentWidthRef.current = width;
            onWidthChange?.(width);
            persistWidth(width);
        };
        if (!handleElement) return;
        handleElement.addEventListener('mousedown', handleMouseDown);
        handleElement.addEventListener('keydown', handleKeyDown);
        return ()=>{
            removeDragShield();
            isResizingRef.current = false;
            handleElement.removeEventListener('mousedown', handleMouseDown);
            handleElement.removeEventListener('keydown', handleKeyDown);
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [
        handleElement,
        minWidth,
        maxWidth,
        responsive,
        onWidthChange,
        persistWidth,
        fraction
    ]);
    // Handle window resize with debouncing
    useEffect(()=>{
        const handleWindowResize = ()=>{
            // Clear existing timeout
            if (resizeTimeoutRef.current) {
                clearTimeout(resizeTimeoutRef.current);
            }
            // Debounce resize handling
            resizeTimeoutRef.current = setTimeout(()=>{
                const viewportWidth = window.innerWidth;
                const maxAllowed = getMaxAllowedWidth(viewportWidth, responsive, maxWidth, fraction);
                // Only adjust if current width exceeds max allowed
                if (currentWidthRef.current > maxAllowed) {
                    const newWidth = clampWidth(currentWidthRef.current, minWidth, maxWidth, responsive, viewportWidth, fraction);
                    currentWidthRef.current = newWidth;
                    onWidthChange?.(newWidth);
                    persistWidth(newWidth);
                }
            }, RESIZE_DEBOUNCE_MS);
        };
        window.addEventListener('resize', handleWindowResize);
        return ()=>{
            window.removeEventListener('resize', handleWindowResize);
            if (resizeTimeoutRef.current) {
                clearTimeout(resizeTimeoutRef.current);
            }
        };
    }, [
        minWidth,
        maxWidth,
        responsive,
        onWidthChange,
        persistWidth,
        fraction
    ]);
    return resizeHandleRef;
};
